// What each status MEANS, in the client's terms — THE single copy.
//
// These strings used to be written inline inside ProjectDrawer's render, which
// was fine while one paragraph was the only thing showing them. They are now
// read twice (that paragraph, and the tooltip on the status pill), and a second
// inline copy is how the two would drift into saying different things about the
// same status.
//
// Culling-only jobs get their own set: the standard copy points at the Review
// tab, which a culling project does not have — it has Delivery. Photo projects
// deliberately use the STANDARD set, because the photo tab is also labelled
// "Review" in the drawer, so the wording lands correctly for them.
//
// STATUS 1 MAKES TWO PROMISES THAT THE BACKEND HAS TO KEEP. Both are true of
// PUT /projects/{id}/assign-editor today:
//   * the email — it calls _notify_client_of_status_change, which sends when the
//     client-facing status actually moves (1 -> 3 on assignment)
//   * the ETA — _calculate_due_date writes `eta` (the client-facing date, editor
//     due + 3 days) in the same handler; it is the "ETA" column on the client's
//     board
// BOTH are gated on the admin track being at 3+ when the editor is assigned:
// assign-editor sets editor status -1 (not 0) below that, the client status does
// not move, so no email fires and no ETA is written. Assigning an editor while
// the board still says "Project Requested" therefore leaves this promise unkept.
// If that gate changes, or the assignment flow stops writing `eta`, this string
// is telling clients something untrue and must change with it.
//
// THE FOOTAGE LINK (Ryan, 2026-10-26; lib/keepLink.js, backend app/keep_link.py).
// We edit straight from a client's own link and keep no copy of their raw
// footage, and "downloading your footage" or "your files have been received" read
// as "they have it now": clients deleted the link. So a client whose footage is a
// link of theirs (`link`, the project's keep_footage_link) reads LINK's sentences
// for 1 and 2, and until the first draft the description asks them to keep it
// (TAILS); at the first draft (5) it says they no longer need to. A hard drive, a
// portal upload or Our Dropbox copy (`link` null) reads the STANDARD sentences,
// which are true there: we do hold that footage. No em dashes in these sentences.
import { KEEP, DONE, DONE_LINE, KEEP_TAIL } from './keepLink.js';

const STANDARD = {
  1: "We're checking your project details and getting your footage ready for your editor. You'll get an email once it's assigned to an editor, and an ETA appears on your project at the same time.",
  2: "Your files have been received and are being prepared for your editor.",
  3: "An editor has been assigned to your project and will begin shortly.",
  4: "Your editor is working on your project. You'll receive an email when your draft is ready for review.",
  5: "Your draft is ready. Head to the Review tab to watch it and leave feedback.",
  6: "Your editor is making changes for the next version.",
  7: "A new version is ready for your review in the Review tab.",
  8: "This project is complete. Download your deliverables from the Review tab.",
};

const CULLING = {
  1: "We're checking your project details and getting your footage ready for your editor. You'll get an email once it's assigned to an editor, and an ETA appears on your project at the same time.",
  2: "Your files have been received and are being prepared for your editor.",
  3: "An editor has been assigned to your cull and will begin shortly.",
  4: "Your editor is culling your footage. You'll receive an email when your project file is ready.",
  5: "Your culled project file is ready. Open the Delivery tab to confirm and download it.",
  6: "Your editor is making changes to your project file.",
  7: "An updated project file is ready in the Delivery tab.",
  8: "This project is complete. Download your project file from the Delivery tab.",
};

// A link of the client's own: what really happens at 1 and 2 (the same for a cull).
const LINK = {
  1: "We're checking your project details and your footage link. You'll get an email once it's assigned to an editor, and an ETA appears on your project at the same time.",
  2: "We've checked your footage link and your editor will work straight from it.",
};

// Until the first draft: the ask, on each status before it. Left off (`tail: false`) where the page shows the
// standing line itself right under the description, so the two do not say it twice.
const TAILS = {
  1: KEEP_TAIL,
  2: "Please don't move, rename or delete those files until your first draft is ready.",
  3: KEEP_TAIL,
  4: KEEP_TAIL,
};

// Approved, but the files are gone under the retention policy: the sentence above would tell
// the client to download something the page has just said was removed.
const FILES_REMOVED = "This project is complete. Its files have been removed under our retention policy; the notice above says how to reach us if you need them.";

/**
 * Plain-English meaning of a client status id, or '' when there isn't one.
 * @param {number} statusId        project.client_status_id
 * @param {boolean} isCulling      culling-only project (see lib/culling.js)
 * @param {boolean} filesRemoved   lib/retentionNotice.filesRemoved(project)
 * @param {string|null} link       project.keep_footage_link: 'keep', 'done' or null (lib/keepLink.js)
 * @param {{tail?: boolean}} opts  tail: false leaves the keep ask off (the page shows the standing line itself)
 */
export function clientStatusHelp(statusId, isCulling = false, filesRemoved = false, link = null, { tail = true } = {}) {
  if (filesRemoved && statusId === 8) return FILES_REMOVED;
  const base = ((link === KEEP || link === DONE) && LINK[statusId]) || (isCulling ? CULLING : STANDARD)[statusId] || '';
  if (!base || !tail) return base;
  if (link === KEEP && TAILS[statusId]) return `${base} ${TAILS[statusId]}`;
  if (link === DONE && statusId === 5) return `${base} ${DONE_LINE}`;
  return base;
}
